import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  User, AlertCircle, Loader2, Upload, X, Save, CheckCircle2,
  Lock, Eye, EyeOff,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { PhoneInput } from "react-international-phone";
import "react-international-phone/style.css";
import { toast } from "sonner";
import api from "../../lib/api";

const BACKEND_URL  = process.env.REACT_APP_BACKEND_URL || "";
const PHOTO_MAX_MB = parseInt(process.env.REACT_APP_PHOTO_MAX_MB || "5", 10);

const GOALS     = ["Muscle Building","Weight Loss","Weight Gain","General Fitness","Other"];
const RELATIONS = ["Father","Mother","Spouse","Brother","Sister","Friend","Other"];
const GENDERS   = ["Male","Female","Other"];

import { COUNTRIES, getStatesForCountry, getStateLabel } from "../../lib/locationData";

const DAYS   = Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, "0"));
const MONTHS = [
  { v:"01",l:"January" },{ v:"02",l:"February" },{ v:"03",l:"March" },
  { v:"04",l:"April" },  { v:"05",l:"May" },     { v:"06",l:"June" },
  { v:"07",l:"July" },   { v:"08",l:"August" },  { v:"09",l:"September" },
  { v:"10",l:"October" },{ v:"11",l:"November" },{ v:"12",l:"December" },
];
const CY   = new Date().getFullYear();
const YEARS = Array.from({ length: CY - 1919 }, (_, i) => String(CY - i));

function SectionCard({ title, children }) {
  return (
    <Card className="bg-card border-border">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-heading">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">{children}</CardContent>
    </Card>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="text-sm">
        {label}{required && <span className="text-destructive ml-0.5">*</span>}
      </Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function PasswordInput({ value, onChange, placeholder }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input
        type={show ? "text" : "password"}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="rounded-lg pr-10"
      />
      <button
        type="button"
        onClick={() => setShow(s => !s)}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
        tabIndex={-1}
      >
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}

// Helper: write key fields back to localStorage so MemberLayout avatar stays in sync
function syncLocalStorage(fields) {
  const stored = JSON.parse(localStorage.getItem("member_data") || "{}");
  localStorage.setItem("member_data", JSON.stringify({ ...stored, ...fields }));
}

export default function MemberProfile() {
  const navigate    = useNavigate();
  const [loading,   setLoading]   = useState(true);
  const [saving,    setSaving]    = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error,     setError]     = useState("");
  const [saved,     setSaved]     = useState(false);

  const [pwForm,    setPwForm]    = useState({ current: "", newPw: "", confirm: "" });
  const [pwSaving,  setPwSaving]  = useState(false);
  const [pwSaved,   setPwSaved]   = useState(false);

  const [dobDay,   setDobDay]   = useState("");
  const [dobMonth, setDobMonth] = useState("");
  const [dobYear,  setDobYear]  = useState("");
  const [photoPreview, setPhotoPreview] = useState(null);

  const [form, setForm] = useState({
    name: "", email: "", gender: "Male", address: "", state: "", city: "",
    pincode: "", country: "India", photo: "",
    emergencyContactName: "", emergencyContactRelation: "", emergencyContactPhone: "",
    medicalIssue: "", goal: "General Fitness", height: "", weight: "",
    professional: "", reference: "", notes: "",
  });

  const token = localStorage.getItem("member_token");

  useEffect(() => {
    if (!token) { navigate("/"); return; }
    (async () => {
      try {
        const res = await api.get("/member-portal/profile", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const m = res.data.member;
        if (m.dob) {
          const parts = m.dob.split("-");
          if (parts.length === 3) {
            setDobYear(parts[0]); setDobMonth(parts[1]); setDobDay(parts[2]);
          }
        }
        const profileData = {
          name:                     m.name                     || "",
          email:                    m.email                    || "",
          gender:                   m.gender                   || "Male",
          address:                  m.address                  || "",
          state:                    m.state                    || "",
          city:                     m.city                     || "",
          pincode:                  m.pincode                  || "",
          country:                  m.country                  || "India",
          photo:                    m.photo                    || "",
          emergencyContactName:     m.emergencyContactName     || "",
          emergencyContactRelation: m.emergencyContactRelation || "",
          emergencyContactPhone:    m.emergencyContactPhone    || "",
          medicalIssue:             m.medicalIssue             || "",
          goal:                     m.goal                     || "General Fitness",
          height:                   m.height != null ? String(m.height) : "",
          weight:                   m.weight != null ? String(m.weight) : "",
          professional:             m.professional             || "",
          reference:                m.reference                || "",
          notes:                    m.notes                    || "",
        };
        setForm(profileData);
        // ✅ Sync photo (and name) to localStorage so sidebar avatar updates immediately
        syncLocalStorage({ name: m.name || "", photo: m.photo || "" });
      } catch (err) {
        if (err?.response?.status === 401) { navigate("/"); return; }
        setError("Failed to load profile.");
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate, token]);

  const upd = (key, val) => setForm(p => ({ ...p, [key]: val }));

  const handlePhoto = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > PHOTO_MAX_MB * 1024 * 1024) {
      toast.error(`Photo must be under ${PHOTO_MAX_MB} MB.`); return;
    }
    setPhotoPreview(URL.createObjectURL(file));
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    if (form.photo) fd.append("old_url", form.photo);
    try {
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      upd("photo", res.data.url);
      toast.success("Photo uploaded");
    } catch {
      toast.error("Photo upload failed"); setPhotoPreview(null);
    } finally { setUploading(false); }
  };
  const removePhoto = () => { setPhotoPreview(null); upd("photo", ""); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const dob = (dobYear && dobMonth && dobDay) ? `${dobYear}-${dobMonth}-${dobDay}` : undefined;
      await api.patch("/member-portal/profile", {
        ...form,
        ...(dob ? { dob } : {}),
        height: form.height !== "" ? Number(form.height) : null,
        weight: form.weight !== "" ? Number(form.weight) : null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      // ✅ Sync name AND photo so sidebar avatar reflects changes immediately
      syncLocalStorage({ name: form.name, photo: form.photo });
      toast.success("Profile updated!");
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save profile.");
    } finally { setSaving(false); }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!pwForm.current) { toast.error("Enter your current password"); return; }
    if (!pwForm.newPw)   { toast.error("Enter a new password"); return; }
    if (pwForm.newPw.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (pwForm.newPw !== pwForm.confirm) { toast.error("Passwords do not match"); return; }
    setPwSaving(true);
    try {
      await api.post("/member-portal/change-password", {
        currentPassword: pwForm.current,
        newPassword:     pwForm.newPw,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Password changed successfully!");
      setPwForm({ current: "", newPw: "", confirm: "" });
      setPwSaved(true);
      setTimeout(() => setPwSaved(false), 3000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to change password.");
    } finally { setPwSaving(false); }
  };

  const busy = saving || uploading;

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
  if (error) return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] gap-3 text-muted-foreground">
      <AlertCircle className="h-8 w-8" /><p>{error}</p>
    </div>
  );

  const photoSrc = photoPreview || (form.photo ? `${BACKEND_URL}${form.photo}` : null);

  return (
    <div className="flex flex-col gap-5 pb-24 lg:pb-10">
      {/* ── Profile Form ── */}
      <form onSubmit={handleSubmit} className={busy ? "opacity-70 pointer-events-none" : ""}>
        <div className="flex items-center justify-between gap-3 mb-5">
          <div>
            <h1 className="text-2xl font-black font-heading uppercase tracking-wide">Profile</h1>
            <p className="text-muted-foreground text-sm mt-0.5">Update your personal information</p>
          </div>
          <Button type="submit" disabled={busy} size="sm" className="gap-2 shrink-0">
            {saving
              ? <><Loader2 className="h-4 w-4 animate-spin" />Saving…</>
              : saved
                ? <><CheckCircle2 className="h-4 w-4" />Saved!</>
                : <><Save className="h-4 w-4" />Save</>}
          </Button>
        </div>

        <div className="flex flex-col gap-5">
          {/* Personal Info */}
          <SectionCard title="Personal Info">
            <div className="flex items-center gap-4">
              <div className="relative w-20 h-20 rounded-xl bg-secondary flex items-center justify-center overflow-hidden border border-border shrink-0">
                {photoSrc
                  ? <img src={photoSrc} alt="" className="w-full h-full object-cover" />
                  : <User className="h-7 w-7 text-muted-foreground" />}
                {photoSrc && (
                  <button type="button" onClick={removePhoto}
                    className="absolute top-0.5 right-0.5 bg-black/60 rounded-full p-0.5 text-white">
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
              <div className="space-y-1">
                <label htmlFor="photo-upload"
                  className={`cursor-pointer text-primary text-sm font-medium hover:underline ${
                    uploading ? "opacity-50 pointer-events-none" : ""
                  }`}>
                  {uploading ? "Uploading…" : (form.photo ? "Change Photo" : "Upload Photo")}
                </label>
                <p className="text-xs text-muted-foreground">Max {PHOTO_MAX_MB} MB · JPG / PNG</p>
                <input id="photo-upload" type="file" accept="image/*" className="hidden" onChange={handlePhoto} />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Full Name" required>
                <Input value={form.name} onChange={e => upd("name", e.target.value)} className="rounded-lg" />
              </Field>
              <Field label="Email">
                <Input type="email" value={form.email} onChange={e => upd("email", e.target.value)} className="rounded-lg" />
              </Field>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Gender">
                <Select value={form.gender} onValueChange={v => upd("gender", v)}>
                  <SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger>
                  <SelectContent>{GENDERS.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Date of Birth">
                <div className="grid grid-cols-3 gap-2">
                  <Select value={dobDay} onValueChange={setDobDay}>
                    <SelectTrigger className="rounded-lg"><SelectValue placeholder="Day" /></SelectTrigger>
                    <SelectContent>{DAYS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={dobMonth} onValueChange={setDobMonth}>
                    <SelectTrigger className="rounded-lg"><SelectValue placeholder="Month" /></SelectTrigger>
                    <SelectContent>{MONTHS.map(m => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={dobYear} onValueChange={setDobYear}>
                    <SelectTrigger className="rounded-lg"><SelectValue placeholder="Year" /></SelectTrigger>
                    <SelectContent>{YEARS.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </Field>
            </div>
          </SectionCard>

          {/* Address */}
          <SectionCard title="Address">
            <Field label="Address">
              <Textarea value={form.address} onChange={e => upd("address", e.target.value)} className="rounded-lg" rows={2} />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label={getStateLabel(form.country)}>
                {getStatesForCountry(form.country).length > 0 ? (
                  <Select value={form.state} onValueChange={v => upd("state", v)}>
                    <SelectTrigger className="rounded-lg"><SelectValue placeholder={`Select ${getStateLabel(form.country)}`} /></SelectTrigger>
                    <SelectContent>
                      {getStatesForCountry(form.country).map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input value={form.state} onChange={e => upd("state", e.target.value)} className="rounded-lg" placeholder={`Enter ${getStateLabel(form.country)}`} />
                )}
              </Field>
              <Field label="City">
                <Input value={form.city} onChange={e => upd("city", e.target.value)} className="rounded-lg" />
              </Field>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Pincode">
                <Input type="number" inputMode="numeric" value={form.pincode} onChange={e => upd("pincode", e.target.value)} className="rounded-lg" />
              </Field>
              <Field label="Country">
                <Select value={form.country} onValueChange={v => { upd("country", v); upd("state", ""); }}>
                  <SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger>
                  <SelectContent>{COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </div>
          </SectionCard>

          {/* Emergency Contact */}
          <SectionCard title="Emergency Contact">
            <Field label="Contact Name">
              <Input value={form.emergencyContactName} onChange={e => upd("emergencyContactName", e.target.value)} className="rounded-lg" />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Relation">
                <Select value={form.emergencyContactRelation} onValueChange={v => upd("emergencyContactRelation", v)}>
                  <SelectTrigger className="rounded-lg"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{RELATIONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Phone">
                <PhoneInput
                  defaultCountry="in"
                  value={form.emergencyContactPhone}
                  onChange={v => upd("emergencyContactPhone", v || "")}
                  inputClassName="flex h-10 w-full rounded-lg border border-input bg-secondary px-3 py-2 text-sm focus-within:ring-2 focus-within:ring-primary/30"
                  className="mt-1"
                />
              </Field>
            </div>
          </SectionCard>

          {/* Fitness & Health */}
          <SectionCard title="Fitness & Health">
            <Field label="Goal">
              <Select value={form.goal} onValueChange={v => upd("goal", v)}>
                <SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger>
                <SelectContent>{GOALS.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Height (cm)">
                <Input type="number" inputMode="decimal" min={0} step="0.1" value={form.height}
                  onChange={e => upd("height", e.target.value)} placeholder="e.g. 170" className="rounded-lg" />
              </Field>
              <Field label="Weight (kg)">
                <Input type="number" inputMode="decimal" min={0} step="0.1" value={form.weight}
                  onChange={e => upd("weight", e.target.value)} placeholder="e.g. 68" className="rounded-lg" />
              </Field>
            </div>
            <Field label="Medical Issues">
              <Textarea value={form.medicalIssue} onChange={e => upd("medicalIssue", e.target.value)} className="rounded-lg" rows={2} />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Profession">
                <Input value={form.professional} onChange={e => upd("professional", e.target.value)} className="rounded-lg" />
              </Field>
              <Field label="Reference">
                <Input value={form.reference} onChange={e => upd("reference", e.target.value)} className="rounded-lg" />
              </Field>
            </div>
            <Field label="Notes">
              <Textarea value={form.notes} onChange={e => upd("notes", e.target.value)} className="rounded-lg" rows={2} />
            </Field>
          </SectionCard>
        </div>
      </form>

      {/* ── Change Password ── */}
      <form onSubmit={handleChangePassword}>
        <Card className="bg-card border-border">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Lock className="h-4 w-4 text-muted-foreground" />
              <CardTitle className="text-base font-heading">Change Password</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <Field label="Current Password" required>
              <PasswordInput
                value={pwForm.current}
                onChange={e => setPwForm(p => ({ ...p, current: e.target.value }))}
                placeholder="Enter current password"
              />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="New Password" required>
                <PasswordInput
                  value={pwForm.newPw}
                  onChange={e => setPwForm(p => ({ ...p, newPw: e.target.value }))}
                  placeholder="Min. 6 characters"
                />
              </Field>
              <Field label="Confirm New Password" required>
                <PasswordInput
                  value={pwForm.confirm}
                  onChange={e => setPwForm(p => ({ ...p, confirm: e.target.value }))}
                  placeholder="Re-enter new password"
                />
              </Field>
            </div>
            {pwForm.confirm && (
              <p className={`text-xs font-medium ${
                pwForm.newPw === pwForm.confirm ? "text-green-500" : "text-destructive"
              }`}>
                {pwForm.newPw === pwForm.confirm ? "✓ Passwords match" : "✗ Passwords do not match"}
              </p>
            )}
            <div className="flex justify-end">
              <Button type="submit" disabled={pwSaving} size="sm" variant="outline" className="gap-2">
                {pwSaving
                  ? <><Loader2 className="h-4 w-4 animate-spin" />Updating…</>
                  : pwSaved
                    ? <><CheckCircle2 className="h-4 w-4 text-green-500" />Updated!</>
                    : <><Lock className="h-4 w-4" />Update Password</>}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>

      {/* Sticky bottom save on mobile */}
      <div className="fixed bottom-0 left-0 right-0 lg:hidden bg-card border-t border-border px-4 py-3 z-30">
        <Button type="button" disabled={busy} className="w-full gap-2" onClick={handleSubmit}>
          {saving
            ? <><Loader2 className="h-4 w-4 animate-spin" />Saving…</>
            : saved
              ? <><CheckCircle2 className="h-4 w-4" />Saved!</>
              : <><Save className="h-4 w-4" />Save Profile</>}
        </Button>
      </div>
    </div>
  );
}
